import type { ColumnDef, SortingState } from '@tanstack/react-table';
import axios from 'axios';
import cx from 'clsx';
import { dateToYMDHM } from 'common/date.ts';
import { fromQuerystring, POST, parseFormValues, toQuerystring, truncate } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { ApiKey } from 'types/ApiKey.ts';
import { Button } from 'ui/component/Button.tsx';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import { IAdd, IOpenLink, ISearch } from 'ui/component/Icons.tsx';
import { Input } from 'ui/control/Input.tsx';
import { usePageSizeInUrl } from 'ui/hook/usePageSizeInUrl.ts';
import { useRestoreScrollPositionOnBack } from 'ui/hook/useRestoreScrollPositionOnBack.ts';
import { ApiKeyForm } from '#admin/component/ApiKeyForm.tsx';
import { useConfirm } from '#admin/component/DialogProvider.tsx';
import { getSWRKeyForPage, Table } from '#admin/component/table/index.ts';

export const getColumns = (t: any, onRevoke: (apiKey: ApiKey) => void) => {
  const columns: ColumnDef<ApiKey>[] = [
    {
      accessorKey: 'id',
      header: t('views.apiKeyList.columns.id'),
      cell: (info) => {
        const id = info.getValue<string>();
        return (
          <CopyableText className="font-mono" copyText={id}>
            {truncate(id, 8, '')}
          </CopyableText>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: t('views.apiKeyList.columns.creationDate'),
      cell: (info) => {
        const value = info.getValue<string>();
        return value ? dateToYMDHM(value) : null;
      },
    },
    {
      accessorKey: 'label',
      header: t('views.apiKeyList.columns.label'),
      meta: { truncate: true },
    },
    {
      accessorKey: 'key',
      header: t('views.apiKeyList.columns.key'),
      meta: { truncate: true },
    },
    {
      id: 'actions',
      header: t('views.apiKeyList.columns.actions'),
      meta: { hideFallback: true },
      cell: (info) => (
        <Button
          className="red"
          onClick={() => {
            if (onRevoke) onRevoke(info.row.original);
          }}
        >
          {t('views.apiKeyList.buttons.revoke')}
        </Button>
      ),
    },
  ];

  return columns;
};

type FormValues = {
  search: string;
};

export const APIKeyList = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRestoreScrollPositionOnBack('api-keys-scroll');
  const [openApiKeyForm, setOpenApiKeyForm] = React.useState<boolean>(false);
  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'label', desc: false }]);

  const defaultValues: FormValues = { search: '' };
  const defaultValuesWithSearchValues = { ...defaultValues, ...fromQuerystring(searchParams, { excludeKeys: ['size'] }) };
  const [values, setValues] = React.useState<typeof defaultValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { handleSubmit, formState } = formContext;

  const { data: rowsCount, mutate: rowsCountMutate } = useSWR(formState.isValid ? ['/api-key/count', values, parseFormValues] : null, POST);
  const swr = useSWRInfinite(getSWRKeyForPage('/api-key/list', 25, values, sorters, parseFormValues), POST, {
    revalidateFirstPage: false,
  });
  const setSizeInUrl = usePageSizeInUrl(swr.setSize);

  const onSubmit = async (values: FormValues) => {
    setSearchParams(toQuerystring(values), { replace: true });
    setValues(values);
    await swr.mutate();
  };

  const handleAddApiKey = () => {
    setOpenApiKeyForm(true);
  };

  const handleAddApiKeyClose = () => {
    setOpenApiKeyForm(false);
    void rowsCountMutate();
    void swr.mutate();
  };

  const columns = React.useMemo(
    () =>
      getColumns(t, async ({ id, label }) => {
        if (!(await confirm(t('views.apiKeyList.confirmMessages.revokeKey', { label })))) return;
        await axios.delete(`/api-key/${id}`);
        swr.mutate();
      }),
    [swr, t, confirm],
  );

  return (
    <main className="view-container">
      <ApiKeyForm isOpen={openApiKeyForm} onClose={handleAddApiKeyClose} onSave={handleAddApiKeyClose} />

      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('views.apiKeyList.title')}</h4>

        <div className="flex flex-row gap-4">
          <Link to={new URL('/docs', import.meta.env.VITE_API_URL).href} target="_blank">
            <Button className="blue-outlined" RightIcon={IOpenLink}>
              {t('views.apiKeyList.buttons.apiReference')}
            </Button>
          </Link>
          <Button className="blue" LeftIcon={IAdd} onClick={handleAddApiKey}>
            {t('views.apiKeyList.buttons.generate')}
          </Button>
        </div>
      </section>

      <section className="mt-4 bg-white px-8 py-4">
        <FormProvider {...formContext}>
          <form id="search-form" ref={formRef} className="my-4 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="flex place-content-between place-items-center">
              <Input
                className="w-[360px]"
                name={'search'}
                placeholder={t('views.apiKeyList.search.placeholder')}
                required={true}
                autoFocus={true}
                RightIcon={ISearch}
                autoComplete={'off'}
                debouncedAutoSubmitForm={'#search-form'}
                debouncedAutoSubmitTimeout={500}
              />
            </div>
          </form>
        </FormProvider>

        <Table
          scrollRef={scrollRef}
          className={cx('max-h-[calc(100vh-385px)]')}
          columns={columns}
          swr={{ ...swr, setSize: setSizeInUrl }}
          pagination={true}
          rowsCount={rowsCount}
          defaultSorting={sorters}
          onColumnSort={setSorters}
          estimateSize={50}
          overscan={50}
        />
      </section>
    </main>
  );
};
